import React, { useState, useEffect } from 'react';
import styles from './MonthSelector.module.css';
import { Modal, Box } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';

interface MonthSelectorProps {
  month?: number; // 0-based month (0 = January)
  year?: number;
  onMonthYearChange?: (month: number, year: number) => void;
}

export const MonthSelector: React.FC<MonthSelectorProps> = ({
  month,
  year,
  onMonthYearChange,
}) => {
  // Internal state if not controlled
  const [selectedMonth, setSelectedMonth] = useState<number>(
    month !== undefined ? month : moment().month()
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    year !== undefined ? year : moment().year()
  );
  const [open, setOpen] = useState(false);

  // Keep internal state in sync with controlled props
  useEffect(() => {
    if (month !== undefined) setSelectedMonth(month);
    if (year !== undefined) setSelectedYear(year);
  }, [month, year]);

  const handleLabelClick = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleMonthChange = (value: Moment | null) => {
    if (value) {
      setSelectedMonth(value.month());
      setSelectedYear(value.year());
      onMonthYearChange?.(value.month(), value.year());
    }
    setOpen(false);
  };

  const handlePrev = () => {
    let newMonth = selectedMonth - 1;
    let newYear = selectedYear;
    if (newMonth < 0) {
      newMonth = 11;
      newYear -= 1;
    }
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
    onMonthYearChange?.(newMonth, newYear);
  };

  const handleNext = () => {
    let newMonth = selectedMonth + 1;
    let newYear = selectedYear;
    if (newMonth > 11) {
      newMonth = 0;
      newYear += 1;
    }
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
    onMonthYearChange?.(newMonth, newYear);
  };

  const monthName = moment().month(selectedMonth).format('MMMM');
  const defaultValue = moment({ year: selectedYear, month: selectedMonth, day: 1 });

  return (
    <div className={styles.container}>
      <button className={styles.arrowButton} onClick={handlePrev} aria-label="Previous Month">
        <span className={styles.arrow}>&lt;</span>
      </button>
      <div className={styles.label} onClick={handleLabelClick} tabIndex={0} role="button">
        {monthName} {selectedYear}
      </div>
      <button className={styles.arrowButton} onClick={handleNext} aria-label="Next Month">
        <span className={styles.arrow}>&gt;</span>
      </button>
      <Modal open={open} onClose={handleClose}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 2, borderRadius: 2 }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateCalendar
              defaultValue={defaultValue}
              views={['month', 'year']}
              openTo="month"
              onChange={handleMonthChange}
            />
          </LocalizationProvider>
        </Box>
      </Modal>
    </div>
  );
};
